import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { speechApi } from '../../utils/api';

// Debug logging (set to false to silence verbose logs)
const DEBUG_VOICE = false;
const log = (...args: any[]) => DEBUG_VOICE && console.log(...args);

interface VoiceAnnotationProps {
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onPauseRecording?: () => void;
  onClearRecording?: () => void;
  onTranscriptionUpdate: (text: string) => void;
  onTranscriptionComplete: (finalText: string) => void;
}

export const VoiceAnnotation: React.FC<VoiceAnnotationProps> = ({
  isRecording,
  onStartRecording,
  onStopRecording,
  onPauseRecording,
  onClearRecording,
  onTranscriptionUpdate,
  onTranscriptionComplete
}) => {
  const [transcription, setTranscription] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
  const finalTranscriptRef = useRef('');
  const isClosedRef = useRef(false);
  const isOperatingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Initialize Azure Speech SDK
  const initializeSpeechSDK = async () => {
    try {
      log('VoiceAnnotation: Initializing Azure Speech SDK...');
      setIsInitializing(true);
      setError(null);
      
      // IMPORTANT: Close and dispose of any existing recognizer first
      if (recognizerRef.current && !isClosedRef.current) {
        try {
          recognizerRef.current.stopContinuousRecognitionAsync(
            () => {
              if (recognizerRef.current) {
                recognizerRef.current.close();
                recognizerRef.current = null;
              }
            },
            () => {
              if (recognizerRef.current) {
                recognizerRef.current.close();
                recognizerRef.current = null;
              }
            }
          );
        } catch (e) {
          // If stopping fails, force close anyway
          if (recognizerRef.current) {
            recognizerRef.current.close();
            recognizerRef.current = null;
          }
        }
        // Wait a bit for cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      isClosedRef.current = false;

      // Get Azure Speech token from server
      log('VoiceAnnotation: Getting speech token from server...');
      const tokenResponse = await speechApi.getToken();
      
      log('VoiceAnnotation: Token response:', tokenResponse.data);
      
      if (!tokenResponse.data.success || !tokenResponse.data.data) {
        throw new Error('Failed to get speech token from server');
      }

      const { token, region } = tokenResponse.data.data;
      log('VoiceAnnotation: Got token for region:', region);

      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
      speechConfig.speechRecognitionLanguage = 'en-US';
      speechConfig.enableDictation();

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      log('VoiceAnnotation: Speech recognizer created');

      // Handle intermediate results (real-time transcription)
      recognizer.recognizing = (s, e) => {
        if (e.result.text && e.result.text.trim()) {
          console.log('🎙️ Speech recognizing:', e.result.text);
          // For intermediate results, show final + current intermediate (but don't save intermediate)
          const currentText = finalTranscriptRef.current + (finalTranscriptRef.current ? ' ' : '') + e.result.text;
          console.log('📝 Setting transcription to:', currentText.trim());
          setTranscription(currentText.trim());
          onTranscriptionUpdate(currentText.trim());
        }
      };

      // Handle final results
      recognizer.recognized = (s, e) => {
        log('Speech recognized:', e.result.text, 'Reason:', e.result.reason);
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech && e.result.text) {
          const trimmedText = e.result.text.trim();
          if (trimmedText) {
            // Always accumulate - just add the new text
            const currentTranscript = finalTranscriptRef.current.trim();
            const newFinalText = currentTranscript + (currentTranscript ? ' ' : '') + trimmedText;
            finalTranscriptRef.current = newFinalText;
            log('Accumulated final transcript:', finalTranscriptRef.current);
            setTranscription(finalTranscriptRef.current);
            onTranscriptionUpdate(finalTranscriptRef.current);
            // Show voice note in console for debugging
            if (finalTranscriptRef.current) {
              console.log(`🎤 Voice note: "${finalTranscriptRef.current}"`);
            }
          }
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
          log('Speech could not be recognized');
        }
      };

      // Handle session events
      recognizer.sessionStarted = (s, e) => {
        // Speech recognition session started
      };

      recognizer.sessionStopped = (s, e) => {
        // Speech recognition session stopped
        onTranscriptionComplete(finalTranscriptRef.current);
      };

      // Handle errors
      recognizer.canceled = (s, e) => {
        console.error('Speech recognition canceled:', e.reason);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          setError(`Speech recognition error: ${e.errorDetails}`);
        }
        recognizer.stopContinuousRecognitionAsync();
      };

      recognizerRef.current = recognizer;
      log('VoiceAnnotation: Initialization complete');
      setIsInitializing(false);
    } catch (error: any) {
      console.error('Failed to initialize speech recognition:', error);
      setError(error.message || 'Failed to initialize speech recognition');
      setIsInitializing(false);
    }
  };

  // Start recording
  const startRecording = useCallback(async (clearTranscription = true) => {
    if (!isMountedRef.current || isOperatingRef.current) {
      log('VoiceAnnotation: Skipping start - not mounted or already operating');
      return;
    }
    
    isOperatingRef.current = true;
    log('VoiceAnnotation: Start recording called, clearTranscription:', clearTranscription);
    
    try {
      // Only clear transcription if explicitly requested (not on resume)
      if (clearTranscription) {
        finalTranscriptRef.current = '';
        setTranscription('');
        log('VoiceAnnotation: Cleared all transcription state');
      } else {
        log('VoiceAnnotation: Resuming with existing transcription:', finalTranscriptRef.current);
      }
      setError(null);
      
      // If we don't have a recognizer or it's closed, create a new one
      if (!recognizerRef.current || isClosedRef.current) {
        log('VoiceAnnotation: Initializing new recognizer...');
        await initializeSpeechSDK();
      }

      // Now start continuous recognition
      if (recognizerRef.current && !isClosedRef.current && isMountedRef.current) {
        try {
          log('VoiceAnnotation: Starting continuous recognition...');

          // Start continuous recognition
          recognizerRef.current.startContinuousRecognitionAsync(
            () => {
              log('VoiceAnnotation: Speech recognition started successfully');
              if (isMountedRef.current) {
                onStartRecording();
              }
              isOperatingRef.current = false;
            },
            (error) => {
              console.error('VoiceAnnotation: Failed to start speech recognition:', error);
              if (isMountedRef.current) {
                setError('Failed to start recording. Please check microphone permissions.');
              }
              isOperatingRef.current = false;
            }
          );
        } catch (error: any) {
          console.error('VoiceAnnotation: Error starting recording:', error);
          if (isMountedRef.current) {
            setError(error.message || 'Failed to start recording');
          }
          isOperatingRef.current = false;
        }
      } else {
        console.error('VoiceAnnotation: No recognizer available after initialization');
        if (isMountedRef.current) {
          setError('Speech recognition not available. Please check your internet connection.');
        }
        isOperatingRef.current = false;
      }
    } catch (error) {
      console.error('VoiceAnnotation: Error in startRecording:', error);
      isOperatingRef.current = false;
    }
  }, [onStartRecording]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (!isMountedRef.current) {
      log('VoiceAnnotation: Skipping stop - not mounted');
      return;
    }
    
    if (recognizerRef.current && !isClosedRef.current) {
      try {
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            log('Speech recognition stopped successfully');
            if (isMountedRef.current) {
              onStopRecording();
            }
          },
          (error) => {
            console.error('Failed to stop speech recognition:', error);
            if (isMountedRef.current) {
              onStopRecording();
            }
          }
        );
      } catch (error) {
        console.error('Error stopping recognition:', error);
        if (isMountedRef.current) {
          onStopRecording();
        }
      }
    } else {
      // No recognizer to stop, just call the callback
      onStopRecording();
    }
  }, [onStopRecording]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (!isMountedRef.current) {
      log('VoiceAnnotation: Skipping pause - not mounted');
      return;
    }
    
    if (recognizerRef.current && isRecording && !isClosedRef.current) {
      try {
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            log('Speech recognition paused - keeping transcription');
            // Don't clear transcription when pausing, just stop listening
            if (onPauseRecording) onPauseRecording();
          },
          (error) => {
            console.error('Failed to pause speech recognition:', error);
            if (onPauseRecording) onPauseRecording();
          }
        );
      } catch (error) {
        console.error('Error pausing recognition:', error);
        if (onPauseRecording) onPauseRecording();
      }
    } else if (onPauseRecording) {
      // No recognizer or not recording, just call the callback
      onPauseRecording();
    }
  }, [isRecording, onPauseRecording]);

  // Clear recording
  const clearRecording = useCallback(() => {
    log('VoiceAnnotation: Clearing recording');
    finalTranscriptRef.current = '';
    setTranscription('');
    setError(null);
    if (onClearRecording) onClearRecording();
  }, [onClearRecording]);

  // Handle button click
  const handleToggleRecording = () => {
    log('VoiceAnnotation: handleToggleRecording called, isRecording:', isRecording);
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // React to isRecording prop changes
  useEffect(() => {
    log('VoiceAnnotation: isRecording prop changed to:', isRecording);
    if (isRecording) {
      log('VoiceAnnotation: isRecording=true, starting recording...');
      // Only clear if there's no existing transcription (true fresh start)
      // If there's text, we're resuming, so don't clear
      // Use finalTranscriptRef instead of transcription state to avoid re-render loop
      const shouldClear = !finalTranscriptRef.current || finalTranscriptRef.current.length === 0;
      log('VoiceAnnotation: Should clear transcription:', shouldClear);
      startRecording(shouldClear);
    } else if (!isRecording && recognizerRef.current && !isClosedRef.current) {
      log('VoiceAnnotation: isRecording=false, stopping recording...');
      stopRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]); // Only depend on isRecording - callbacks are protected by isOperatingRef

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      log('VoiceAnnotation: Unmounting, cleaning up...');
      isMountedRef.current = false;
      
      if (recognizerRef.current && !isClosedRef.current) {
        try {
          recognizerRef.current.close();
          isClosedRef.current = true;
          recognizerRef.current = null;
        } catch (error) {
          console.warn('Error closing recognizer on unmount:', error);
        }
      }
    };
  }, []);

  return (
    <div className="voice-annotation">
      {/* Recording Status - Just above buttons */}
      {isRecording && (
        <div className="absolute bottom-full left-4 right-4 mb-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-white text-base font-medium">Tell us about the item</span>
            </div>
            <div className="flex items-center space-x-2">
              {/* Pause Button */}
              <button
                onClick={pauseRecording}
                className="p-2 bg-yellow-500 hover:bg-yellow-600 rounded-full transition-colors"
                aria-label="Pause recording"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                </svg>
              </button>
              {/* Clear Button */}
              <button
                onClick={clearRecording}
                disabled={!transcription}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Clear recording"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {transcription && (
            <div className="text-white/90 text-base leading-relaxed">
              <p className="italic font-medium">"{transcription}"</p>
            </div>
          )}
          {!transcription && (
            <div className="text-white/70 text-base">
              <p>Speak about this item...</p>
            </div>
          )}
        </div>
      )}

      {/* Error Message - Just above buttons */}
      {error && (
        <div className="absolute bottom-full left-4 right-4 mb-4 bg-red-500/90 backdrop-blur-sm rounded-lg p-4 border border-red-400/50">
          <div className="text-white text-base">
            <p className="font-medium">Recording Error</p>
            <p className="text-sm mt-1 opacity-90">{error}</p>
          </div>
        </div>
      )}

      {/* Paused/Final Transcription Display - Just above buttons */}
      {!isRecording && transcription && (
        <div className="absolute bottom-full left-4 right-4 mb-4 bg-yellow-500/90 backdrop-blur-sm rounded-lg p-4 border border-yellow-400/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-white rounded-full"></div>
              <p className="font-medium text-white">Voice Note Paused</p>
            </div>
            <div className="flex items-center space-x-2">
              {/* Resume Button */}
              <button
                onClick={() => startRecording(false)}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                aria-label="Resume recording"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
              {/* Clear Button */}
              <button
                onClick={clearRecording}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                aria-label="Clear recording"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="text-white text-base">
            <p className="text-sm italic opacity-90">"{transcription}"</p>
          </div>
        </div>
      )}
    </div>
  );
};
